import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Lock, Package, Plus, Route } from 'lucide-react';
import { useVerifiedAction } from '@/components/verification/VerificationDialog';
import { publishOptions } from '@/utils/roles';
import '@/styles/publish-button.css';

const ICONS = { colis: Package, trajet: Route };

/**
 * Bouton de publication, le même dans toute l'application (règle de l'utilisateur du 2026-10-29, `utils/roles.js`) :
 * « Publier un colis » (expéditeur), « Publier un trajet » (transporteur), « Publier une annonce » avec un menu des deux
 * (compte hybride, visiteur) ; rien pour l'admin. Publier exige des vérifications : cadenas sur le bouton, et la fenêtre
 * explique ce qui manque au lieu d'ouvrir une page bloquée.
 *
 * `buttonClassName` : apparence du bouton dans l'écran qui l'accueille ; `testid` : son identifiant de test (le cadenas
 * est `${testid}-lock`, les choix du menu `publish-colis` / `publish-trajet`) ; `shortOnPhone` : « Publier ▾ » sur
 * un petit écran quand il y a un menu (un seul choix garde toujours « colis » ou « trajet » dans son libellé) ;
 * `children` : contenu du bouton à la place du libellé (tuile d'accès rapide du tableau de bord), le menu et la garde
 * restent ceux du composant.
 */
export default function PublishButton({ user, buttonClassName = '', testid = 'btn-publish', shortOnPhone = false, children = null }) {
  const navigate = useNavigate();
  const options = publishOptions(user);
  const colisGuard = useVerifiedAction(user, 'publish', 'publier un colis');
  const trajetGuard = useVerifiedAction(user, 'publish', 'publier un trajet');
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const items = useRef([]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', outside);
    items.current[0]?.focus();
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);

  if (options.length === 0) return null;

  const go = (o) => {
    setOpen(false);
    const guard = o.kind === 'colis' ? colisGuard : trajetGuard;
    if (o.needsVerification) guard.run(() => navigate(o.to));
    else navigate(o.to);
  };
  // Une seule action « publish » pour un colis comme pour un trajet : les deux gardes sont verrouillées ensemble
  const locked = !!user && colisGuard.locked;
  const single = options.length === 1 ? options[0] : null;
  const label = single ? single.label : 'Publier une annonce';
  const [verb, ...rest] = label.split(' ');

  const onMenuKey = (e) => {
    const list = items.current.filter(Boolean);
    const i = list.indexOf(document.activeElement);
    if (e.key === 'Escape') { setOpen(false); wrap.current?.querySelector('button')?.focus(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); list[(i + 1) % list.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); list[(i - 1 + list.length) % list.length]?.focus(); }
  };

  return (
    <div className="pb-wrap" ref={wrap}>
      <button
        type="button"
        className={`pb-btn ${buttonClassName}`}
        data-testid={testid}
        onClick={() => (single ? go(single) : setOpen((o) => !o))}
        {...(single ? {} : { 'aria-haspopup': 'menu', 'aria-expanded': open })}
      >
        {children || (
          <>
            <Plus size={15} aria-hidden="true" />
            <span className="pb-label">
              {verb}{rest.length > 0 && <span className={shortOnPhone && !single ? 'pb-long' : undefined}> {rest.join(' ')}</span>}
            </span>
            {!single && <ChevronDown size={15} aria-hidden="true" className={`pb-chev${open ? ' is-open' : ''}`} />}
          </>
        )}
        {locked && <Lock size={13} className="cta-lock" aria-label="Vérification requise" data-testid={`${testid}-lock`} />}
      </button>
      {open && !single && (
        <div className="pb-menu" role="menu" aria-label="Que voulez-vous publier ?" onKeyDown={onMenuKey}>
          {options.map((o, i) => {
            const Icon = ICONS[o.kind];
            return (
              <button key={o.kind} type="button" role="menuitem" className="pb-item"
                ref={(el) => { items.current[i] = el; }}
                onClick={() => go(o)} data-testid={`publish-${o.kind}`}>
                <Icon size={16} aria-hidden="true" />
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
      {colisGuard.dialog}
      {trajetGuard.dialog}
    </div>
  );
}
